import type { PoGstMode, PurchaseOrderItem } from '../types';

export const NZ_GST_RATE = 0.15;

export interface PoLineGstBreakdown {
  lineId: string;
  lineTotal: number;
  exGst: number;
  gst: number;
  incGst: number;
}

export interface PoTotals {
  subtotalExGst: number;
  gstAmount: number;
  totalIncGst: number;
  lineBreakdown: PoLineGstBreakdown[];
}

export function computeLineTotal(line: PurchaseOrderItem): number {
  return (line.quantity || 0) * (line.costPerUnit || 0);
}

export function computePoTotals(
  items: PurchaseOrderItem[],
  gstMode: PoGstMode = 'inclusive',
): PoTotals {
  const lineBreakdown: PoLineGstBreakdown[] = items.map((line) => {
    const lineTotal = computeLineTotal(line);
    if (gstMode === 'inclusive') {
      const exGst = lineTotal * (100 / 115);
      const gst = lineTotal * (15 / 115);
      return { lineId: line.lineId, lineTotal, exGst, gst, incGst: lineTotal };
    }
    const exGst = lineTotal;
    const gst = lineTotal * NZ_GST_RATE;
    const incGst = exGst + gst;
    return { lineId: line.lineId, lineTotal: exGst, exGst, gst, incGst };
  });

  const subtotalExGst = lineBreakdown.reduce((s, l) => s + l.exGst, 0);
  const gstAmount = lineBreakdown.reduce((s, l) => s + l.gst, 0);
  const totalIncGst = lineBreakdown.reduce((s, l) => s + l.incGst, 0);
  return { subtotalExGst, gstAmount, totalIncGst, lineBreakdown };
}

export function formatNzCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Soft validation for NZ IRD GST number format (XXX-XXX-XXX or digits). */
export function isValidNzGstNumber(gst: string): boolean {
  const cleaned = gst.replace(/\s/g, '');
  if (!cleaned) return false;
  return /^\d{2,3}-\d{3}-\d{3}$/.test(cleaned) || /^\d{8,9}$/.test(cleaned);
}

export function gstModeLabel(mode: PoGstMode): string {
  return mode === 'inclusive' ? 'GST inclusive' : 'GST exclusive';
}
